import { InfoIcon } from 'lucide-react'

import { employersEmployerSettings } from 'rails_routes'
import { Tooltip, TooltipProvider } from 'slab'

import { getBrowserTimezoneDatetimeString } from '../utils'
import PayrollDeductionBanner from './PayrollDeductionBanner'

interface PayrollSyncStatusHeaderProps {
  isConnectedToPayroll: boolean | null
  lastEmployeeSync: Date | null
  nextEmployeeSync: Date | null
  payrollIntegrationProvider: string | null
  supportsAutomaticDeductions: boolean
  hasPayrollIntegration: boolean
  requiresEmployeeDeduplication: boolean
  automaticDeductionsSetupComplete: boolean
  automaticDeductionsEnabled: boolean
  showAutomatedDeductionsNotice?: boolean
}

const PayrollSyncStatusHeader = ({
  isConnectedToPayroll,
  lastEmployeeSync,
  nextEmployeeSync,
  payrollIntegrationProvider,
  supportsAutomaticDeductions,
  hasPayrollIntegration,
  requiresEmployeeDeduplication,
  automaticDeductionsSetupComplete,
  automaticDeductionsEnabled,
  showAutomatedDeductionsNotice,
}: PayrollSyncStatusHeaderProps) => {
  if (!hasPayrollIntegration) return null

  const providerName = payrollIntegrationProvider || 'your payroll provider'

  const formatDate = (date: Date | null) => {
    if (!date) return null
    return getBrowserTimezoneDatetimeString(date)
  }

  const formattedLastSync = formatDate(lastEmployeeSync)
  const formattedNextSync = formatDate(nextEmployeeSync)

  // Determine sync status text
  let syncStatusText = 'Not synced yet'
  if (formattedLastSync) {
    syncStatusText = `Last synced on ${formattedLastSync}`
  } else if (formattedNextSync && isConnectedToPayroll) {
    syncStatusText = `First sync by ${formattedNextSync}`
  }

  const nextSyncInfo = (
    <div>
      Next sync by {formattedNextSync}
    </div>
  )

  const shouldShowTooltip = isConnectedToPayroll && lastEmployeeSync !== null

  return (
    <>
      {supportsAutomaticDeductions && (
        <PayrollDeductionBanner
          lastEmployeeSync={lastEmployeeSync}
          requiresEmployeeDeduplication={requiresEmployeeDeduplication}
          automaticDeductionsSetupComplete={automaticDeductionsSetupComplete}
          automaticDeductionsEnabled={automaticDeductionsEnabled}
          showAutomatedDeductionsNotice={showAutomatedDeductionsNotice}
          payrollProviderName={providerName}
        />
      )}
      <div className='bg-white border border-gray-200 rounded-2xl shadow-sm mb-4'>
        <div className='flex flex-col md:flex-row md:justify-between p-6 gap-y-4 md:gap-x-6 md:items-center'>
          <div className='flex flex-col'>
            <div className='flex items-center'>
              {isConnectedToPayroll
                ? (
                  <>
                    <div className='w-2.5 h-2.5 rounded-full bg-green-400 mr-2 flex-shrink-0' />
                    <span className='body-bold text-gray-800'>
                      Synced from {providerName}
                    </span>
                  </>
                  )
                : (
                  <>
                    <div className='w-2.5 h-2.5 rounded-full bg-red-400 mr-2 flex-shrink-0' />
                    <span className='body-bold text-gray-800'>
                      Not connected to {providerName}
                    </span>
                  </>
                  )}
            </div>
            {!isConnectedToPayroll && (
              <a
                href={employersEmployerSettings.show({ query: { tab: 'payroll' } })}
                className='body-sm text-thatch-purple hover:text-thatch-purple-dark mt-2'
              >
                Reconnect your payroll system
              </a>
            )}
          </div>

          <div className='flex items-center space-x-1'>
            <span className='body-sm text-gray-800'>
              {syncStatusText}
            </span>
            {shouldShowTooltip && (
              <TooltipProvider>
                <Tooltip content={nextSyncInfo}>
                  <InfoIcon aria-label='Next sync by' size={16} />
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default PayrollSyncStatusHeader
